/**
 * Shell Types - типы для модульной архитектуры TLH.ru
 *
 * TLH.ru работает как SuperApp Shell, который:
 * 1. Определяет организацию (до авторизации)
 * 2. Загружает список модулей от backend
 * 3. Динамически монтирует микрофронтенды
 */

import type { LocalizableString, ThemeBranding, ThemeColors } from '@/types/theme'

// ============================================================================
// Модули (микрофронтенды)
// ============================================================================

/**
 * Манифест модуля - метаданные для загрузки и отображения MF
 */
export interface ModuleManifest {
  /** Уникальный ID модуля (tagcloud, presentations, lms, ...) */
  id: string
  /** Локализованное название */
  title: string
  /** Описание модуля */
  description?: string
  /** MDI иконка (mdi-cloud, mdi-school, ...) */
  icon: string
  /** URL entry.js для динамического импорта */
  entryUrl: string
  /** Базовый путь для API модуля */
  basePath: string
  /** Версия модуля (для кеширования) */
  version: string
  /** Требуется ли авторизация */
  requiresAuth: boolean
  /** Настройки меню */
  menu: {
    section: 'main' | 'tools' | 'admin'
    order: number
    /** Скрыть из меню (но доступен по URL) */
    hidden?: boolean
  }
  /** Доступные фичи модуля */
  features?: string[]
  /** Требуемые роли для доступа */
  requiredRoles?: string[]
}

/**
 * Ответ API с модулями организации
 */
export interface OrgModulesResponse {
  /** Данные организации */
  org: OrgContext
  /** Список доступных модулей */
  modules: ModuleManifest[]
  /** Включённые фичи для организации */
  features: string[]
  /** Версии для кеширования */
  versions: VersionsInfo
}

/**
 * Информация о версиях для кеширования
 */
export interface VersionsInfo {
  /** Версия манифеста */
  manifest: string
  /** Версии отдельных модулей */
  modules: Record<string, string>
  /** Временная метка сервера */
  timestamp: string
}

// ============================================================================
// Организация и контекст
// ============================================================================

/**
 * Контекст организации
 */
export interface OrgContext {
  id: string
  slug: string
  /** Название организации (может быть локализовано) */
  name: LocalizableString
  type: 'public' | 'university' | 'school' | 'corporate' | 'custom'
  branding: ThemeBranding
  colors: ThemeColors
  locale?: string
  /** Доступные языки организации */
  languages?: string[]
}

/**
 * Источник определения организации
 */
export type OrgSource = 'domain' | 'subdomain' | 'param' | 'config' | 'default'

/**
 * Результат определения организации
 */
export interface OrgResolution {
  slug: string
  source: OrgSource
}

// ============================================================================
// Пользователь и авторизация
// ============================================================================

/**
 * Контекст пользователя (из JWT)
 */
export interface UserContext {
  id: string
  email?: string
  displayName: string
  avatar?: string
  locale?: string
  /** Роли в текущей организации */
  roles: string[]
  /** Права доступа */
  permissions?: string[]
  /** Провайдер авторизации */
  authProvider?: 'yandex' | 'keycloak'
  /** Дополнительные данные */
  [key: string]: unknown
}

/**
 * Профиль пользователя - персональные данные (PII)
 * Хранится в S3: /orgs/{orgSlug}/users/{userId}/profile.json
 */
export interface UserProfile {
  id: string
  email: string
  displayName: string
  avatar?: string
  phone?: string
  birthDate?: string
  /** OAuth провайдер */
  oauthProvider: 'yandex' | 'keycloak'
  /** ID в OAuth провайдере */
  oauthId: string
  /** Дата создания профиля */
  createdAt: string
  /** Дата последнего обновления */
  updatedAt: string
}

/**
 * Данные портала (не PII) - роли, курсы, badges
 * Хранится в S3: /orgs/{orgSlug}/users/{userId}/portal-data.json
 */
export interface UserPortalData {
  userId: string
  orgSlug: string
  /** Роли пользователя в организации */
  roles: string[]
  /** ID курсов, на которые записан */
  enrolledCourses: string[]
  /** ID завершённых модулей */
  completedModules: string[]
  /** Полученные badges/достижения */
  badges: string[]
  /** Дата последней активности */
  lastActivity: string
}

/**
 * Настройки пользователя
 * Хранится в S3: /orgs/{orgSlug}/users/{userId}/preferences.json
 */
export interface UserPreferences {
  userId: string
  theme: 'light' | 'dark' | 'auto'
  locale: string
  notifications: {
    email: boolean
    push: boolean
    telegram?: boolean
  }
  /** Кастомные настройки по модулям */
  moduleSettings?: Record<string, Record<string, unknown>>
}

/**
 * Лог активности пользователя
 * Хранится в S3: /orgs/{orgSlug}/users/{userId}/activity/logs.json
 */
export interface ActivityLog {
  entries: ActivityLogEntry[]
}

export interface ActivityLogEntry {
  timestamp: string
  action: string
  moduleId?: string
  metadata?: Record<string, unknown>
}

/**
 * Прогресс по курсу
 * Хранится в S3: /orgs/{orgSlug}/users/{userId}/activity/courses/{courseId}.json
 */
export interface CourseProgress {
  courseId: string
  startedAt: string
  completedAt?: string
  /** Прогресс 0-100 */
  progress: number
  lessons: LessonProgress[]
}

export interface LessonProgress {
  lessonId: string
  completedAt?: string
  score?: number
}

// ============================================================================
// Mount Context - контекст для монтирования MF
// ============================================================================

/**
 * Режим запуска Shell
 */
export type ShellMode = 'spa' | 'iframe' | 'messenger' | 'widget'

/**
 * Интерфейс Event Bus для коммуникации между Shell и MF
 */
export interface EventBus {
  on<T = unknown>(event: string, handler: (payload: T) => void): void
  off<T = unknown>(event: string, handler: (payload: T) => void): void
  emit<T = unknown>(event: string, payload: T): void
}

/**
 * API клиент для MF
 */
export interface ApiClient {
  get<T = unknown>(url: string, config?: RequestConfig): Promise<T>
  post<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T>
  put<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T>
  delete<T = unknown>(url: string, config?: RequestConfig): Promise<T>
  patch<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T>
}

export interface RequestConfig {
  headers?: Record<string, string>
  params?: Record<string, string | number | boolean>
  timeout?: number
}

/**
 * Контекст монтирования модуля
 * Передаётся в mountApp() при загрузке MF
 */
export interface MountContext {
  /** DOM-контейнер для монтирования */
  container: HTMLElement
  /** Данные пользователя (если авторизован) */
  user?: UserContext
  /** Контекст организации */
  org: OrgContext
  /** Event Bus для коммуникации */
  bus: EventBus
  /** API клиент с авторизацией */
  apiClient: ApiClient
  /** Базовый путь для API модуля */
  basePath: string
  /** Текущая локаль */
  locale: string
  /** Текущая тема */
  theme: 'light' | 'dark'
  /** Режим запуска */
  mode: ShellMode
  /** Функция получения токена */
  getToken?: () => string | null
  /** Начальный маршрут внутри модуля */
  initialRoute?: string
  /** Дополнительные данные */
  [key: string]: unknown
}

// ============================================================================
// Кеширование
// ============================================================================

/**
 * Обёртка для кешированных данных
 */
export interface CachedData<T> {
  /** Версия данных */
  version: string
  /** Сами данные */
  data: T
  /** Время кеширования */
  cachedAt: string
}

/**
 * Ключи кеша
 */
export const CACHE_KEYS = {
  orgManifest: (slug: string) => `tlh:org:${slug}:manifest`,
  orgModules: (slug: string) => `tlh:org:${slug}:modules`,
  module: (moduleId: string) => `tlh:module:${moduleId}`,
  userProfile: (userId: string) => `tlh:user:${userId}:profile`,
  userPortalData: (userId: string) => `tlh:user:${userId}:portal-data`,
  userPreferences: (userId: string) => `tlh:user:${userId}:preferences`,
} as const

// ============================================================================
// Экспорты MF (что ожидаем от entry.js)
// ============================================================================

/**
 * Экспорты, которые должен предоставлять entry.js модуля
 */
export interface ModuleExports {
  /** Функция монтирования основного приложения */
  mountApp: (ctx: MountContext) => void | Promise<void>
  /** Функция размонтирования всех экземпляров */
  unmountAll: () => void
  /** Версия модуля */
  VERSION?: string
  /** SHA сборки */
  BUILD_SHA?: string
  /** ID модуля */
  MF_ID?: string
}

/**
 * Загруженный модуль в памяти
 */
export interface LoadedModule {
  manifest: ModuleManifest
  version: string
  exports: ModuleExports
  loadedAt: string
}
